/* eslint-disable @typescript-eslint/no-unused-vars */
import { JitsiConferenceEvents, JitsiGenericEvent } from 'nexgen-types/lib-jitsi';
import { getJitsiWhisperService, jitsiService } from '../../../shared/singletons/jitsi-singleton';
import { useMeetingStore } from '../meeting.store';
import { MeetingRoomState } from '../../../shared/interfaces/meeting-service.interface';
import { isJitsiConnectionDisconnectedEvent } from '../../../shared/utils/jitsi/helper-methods';
import { ApiService } from '../../../shared/services/api.service';
import { handleAsync } from '@host/utils/handleAsync';
import { useRecordingStore } from '../recording.store';
import { MeetingService } from '../../../apps/meeting/services/MeetingService';
import { useBreakoutStore } from '../breakout-rooms-store';
import {
  reassignRecordingModerator,
  restoreRecordingModerator,
  reassignTranscriptionModerator,
  restoreTranscriptionModerator,
} from './recording-utils';
import {
  JITSI_DEFAULT_RESOLUTION,
  JITSI_DEFAULT_RESOLUTION_GROUP,
  JITSI_OPTIMAL_VIDEO_CONSTRAINTS,
  JITSI_OPTIMAL_VIDEO_CONSTRAINTS_GROUP,
} from '../../../utils/constants';
import { useMemo } from 'react';
import { useNotificationStore } from '../notification.store';
import { ENTITLEMENT_UPGRADE_MESSAGE } from '@/shared/stores/entitlements.store';
import {
  startAuditoriumRoleSync,
  stopAuditoriumRoleSync,
} from '@/apps/auditorium/services/auditorium-role-sync';
import { readStoredJoinToken } from '@/apps/auditorium/services/auditorium-schedule.service';
import { assemblyAIStreamingService } from '../../../shared/services/AssemblyAIStreamingService';
import { useWorkplaceStore } from '../workplace.store';
import { useGuestStore } from '../guest.store';
import { restoreFloorMapAfterAuditorium } from '../carouselStore';
import { Logger } from '../../../shared/services/logger.service';
import { useChatPanelStore } from '@/apps/rocketchat/store/chatPanel.store';
import { ensureChatDrawerOpen } from '@/apps/rocketchat/utils/openDirectMessage';
import { useMultiFloorStateStore } from '../../../apps/office/stores/MultiFloorStore';
import { OfficeService } from '../../../apps/office/services/OfficeService';
import { useSessionStore } from '../../stores/session.store';
import { useAdministrationStore } from '../administration.store';
import { PolicyTypeEnum } from '../../../shared/enums/policyType.enum';
import {
  consumeGuestVideoOptIn,
  isGuestExemptFromAlwaysVideo,
} from '@host/utils/guestVideoPreference';
import { setChatOpenSource } from '@host/utils/chat-tracking';

export const handleTrackAdded = (track: any) => {
  if (!track || !track.getParticipantId()) return;

  if (track.isLocal && track.isLocal() && isAudioTrack(track)) {
    Logger.debug('[Jitsi] 🎤 Local audio track added');
    assemblyAIStreamingService.attachJitsiTrack(track);
  }

  const participantId = track.getParticipantId();
  const jitsiParticipantInstance = jitsiService?.getParticipantById(participantId);
  const isScreenShare = isScreenTrack(track);
  const trackId = track.stream.id;

  // A share that was already over before we got here must not create a tile.
  //
  // Cleanup of screen shares is otherwise purely event-driven (TRACK_REMOVED, or
  // TRACK_MUTE_CHANGED routed through handleTrackRemoved), which only works for a viewer who
  // was subscribed when the share ended. Someone who refreshes afterwards is a NEW
  // subscriber: they enumerate whatever the conference still advertises, with no transition
  // to react to — so a dead desktop track landed in the store and produced a permanently
  // black tile that nothing could clear. Refuse it at the door instead.
  if (isScreenShare && !track.isLocal?.()) {
    const alreadyMuted = track.isMuted?.() === true;
    const nativeEnded = track.getTrack?.()?.readyState === 'ended';
    if (alreadyMuted || nativeEnded) {
      Logger.info(
        '[Jitsi] Ignoring screen-share track that is already inactive for',
        participantId,
        {
          alreadyMuted,
          nativeEnded,
        }
      );
      return;
    }
  }

  useMeetingStore.setState((state) => {
    // If a track arrives for a participant whose userPid matches the local user,
    // and the participant ID differs from the local one, this is a ghost leftover
    // from a previous connection (e.g. after a page refresh). Skip it.
    if (!track.isLocal()) {
      const userPid = jitsiParticipantInstance?.userPid;
      if (userPid) {
        const localId = Object.keys(state.jitsiParticipants).find(
          (id) =>
            state.jitsiParticipants[id].isLocal && state.jitsiParticipants[id].userPid === userPid
        );
        if (localId && localId !== participantId) {
          Logger.info(
            '[Jitsi] Ignoring ghost track for',
            participantId,
            '(duplicate of local',
            localId + ')'
          );
          return state;
        }
      }
    }

    const existingParticipant = state.jitsiParticipants[participantId] || {
      id: participantId,
      displayName: track.isLocal() ? 'Me' : (jitsiParticipantInstance?.displayName ?? 'Unknown'),
      isLocal: track.isLocal(),
      userPid: jitsiParticipantInstance?.userPid,
      avatarUrl: jitsiParticipantInstance?.avatarUrl,
      avatarThumbnailUrl: jitsiParticipantInstance?.avatarThumbnailUrl,
    };

    const newTrack = {
      id: trackId,
      type: isScreenShare ? 'screen' : getTrackType(track),
      track: track,
    };

    const updatedParticipant = { ...existingParticipant };
    if (isScreenShare) {
      updatedParticipant.screenTrack = newTrack;
    } else if (isAudioTrack(track)) {
      updatedParticipant.audioTrack = newTrack;
    } else if (isVideoTrack(track)) {
      updatedParticipant.videoTrack = newTrack;
    }

    return {
      jitsiParticipants: {
        ...state.jitsiParticipants,
        [participantId]: updatedParticipant,
      },
    };
  });

  if (useMeetingStore.getState().jitsiParticipants[participantId]) {
    useMeetingStore.getState().registerParticipantForOrder(participantId);
  }
};

export const handleTrackRemoved = (track: any) => {
  if (!track || !track.getParticipantId()) return;
  const participantId = getParticipantIdByTrack(track);

  if (track.isLocal() && isAudioTrack(track)) {
    assemblyAIStreamingService.attachJitsiTrack(null);
  }

  useMeetingStore.setState((state) => {
    const participant = state.jitsiParticipants[participantId];
    if (!participant) return state;

    const updatedParticipant = { ...participant };

    // Only clear a slot if the track being removed is still the one occupying it.
    //
    // conference.replaceTrack(old, new) emits TRACK_ADDED for the new track and
    // TRACK_REMOVED for the old one, with no ordering guarantee between them. Clearing
    // unconditionally meant a replace could blank the slot the new track had already
    // filled: the camera keeps streaming, but the store says the participant has no video
    // track, so the tile drops to the avatar. This is on the room-switch path — every join
    // runs applySavedDevicePreferences(), which replaces the camera track opened by
    // initLocalTracks whenever the user's saved camera isn't the system default.
    const stillOccupiedBy = (slot?: { track?: any }) => !slot?.track || slot.track === track;

    if (isScreenTrack(track)) {
      if (stillOccupiedBy(updatedParticipant.screenTrack)) {
        updatedParticipant.screenTrack = undefined;
      }
    } else if (isAudioTrack(track)) {
      if (stillOccupiedBy(updatedParticipant.audioTrack)) {
        updatedParticipant.audioTrack = undefined;
      }
    } else if (isVideoTrack(track)) {
      if (stillOccupiedBy(updatedParticipant.videoTrack)) {
        updatedParticipant.videoTrack = undefined;
      }
    }

    // Do NOT remove the participant when they have no tracks remaining.
    // A user with no camera (audio-only) should stay visible with their avatar.
    // Only handleUserLeft should evict a participant from the store.

    return {
      jitsiParticipants: {
        ...state.jitsiParticipants,
        [participantId]: updatedParticipant,
      },
    };
  });
};

export const handleUserJoined = (participantId?: string, participant?: any) => {
  if (!participantId) return;
  const displayName =
    participant._identity?.user?.name ?? participant?.getDisplayName() ?? 'Unknown';
  const userPid = participant._identity?.user?.id;
  const avatarUrl = participant._identity?.user?.avatar ?? '';
  const avatarThumbnailUrl = participant._identity?.user?.avatar_thumbnail ?? undefined;
  // NGE-681: capture the Jitsi moderator role so the auditorium layout can split
  // presenters (square stage tiles) from the listen-only audience (circular avatars).
  const isModerator =
    typeof participant?.getRole === 'function' ? participant.getRole() === 'moderator' : undefined;

  useMeetingStore.setState((state) => {
    let participants = { ...state.jitsiParticipants };

    // When the same user reconnects after a silent disconnect (network change, browser
    // crash, refresh, etc.), Jitsi may not have fired USER_LEFT for the old connection.
    // The new connection arrives with a different Jitsi participant ID but the same userPid.
    // Remove the stale entry so its tile doesn't stay frozen in the grid.
    if (userPid) {
      const duplicateId = Object.keys(participants).find(
        (id) => id !== participantId && participants[id].userPid === userPid
      );
      if (duplicateId) {
        // If the existing entry is the local user, the incoming participant is a ghost
        // leftover from a previous connection (e.g. after a page refresh). Drop it.
        if (participants[duplicateId].isLocal) {
          Logger.info(
            '[Jitsi] Ignoring ghost participant',
            participantId,
            '(duplicate of local',
            duplicateId + ')'
          );
          return state;
        }

        // Otherwise the existing entry is the stale remote one — remove it.
        Logger.info(
          '[Jitsi] Replacing stale participant',
          duplicateId,
          '→',
          participantId,
          '(userPid:',
          userPid + ')'
        );
        const stale = participants[duplicateId];
        [stale?.audioTrack, stale?.videoTrack, stale?.screenTrack].forEach((t) => {
          if (t?.track) {
            try {
              t.track.detach?.();
            } catch (e) {
              Logger.error('[Jitsi] Error detaching stale track', e);
            }
            try {
              t.track.dispose?.();
            } catch (e) {
              Logger.error('[Jitsi] Error disposing stale track', e);
            }
          }
        });
        const { [duplicateId]: _, ...rest } = participants;
        participants = rest;
        useMeetingStore.getState().unregisterParticipantForOrder(duplicateId);
        Logger.info(
          '[Jitsi] Stale participant replaced, store now has',
          Object.keys(participants).length,
          'participants'
        );
      }
    }

    if (participants[participantId]) {
      return {
        jitsiParticipants: {
          ...participants,
          [participantId]: {
            ...participants[participantId],
            displayName: displayName,
            userPid,
            avatarUrl,
            // The Jitsi identity carries no thumbnail yet (JWT change pending), so
            // keep any existing store value (e.g. local seed) instead of clobbering it.
            avatarThumbnailUrl:
              avatarThumbnailUrl ?? participants[participantId].avatarThumbnailUrl,
            ...(isModerator !== undefined && { isModerator }),
          },
        },
      };
    }

    return {
      jitsiParticipants: {
        ...participants,
        [participantId]: {
          id: participantId,
          displayName,
          isLocal: false,
          userPid,
          avatarUrl,
          avatarThumbnailUrl,
          isModerator,
        },
      },
    };
  });

  if (useMeetingStore.getState().jitsiParticipants[participantId]) {
    useMeetingStore.getState().registerParticipantForOrder(participantId);
  }

  if (userPid) {
    restoreRecordingModerator(userPid);
    restoreTranscriptionModerator(userPid);
  }

  // Downgrade resolution on bigger meetings
  const meetingSize = Object.keys(useMeetingStore.getState().jitsiParticipants).length;
  if (meetingSize >= 6 && jitsiService?.currentResolution !== JITSI_DEFAULT_RESOLUTION_GROUP) {
    jitsiService!.upgradeVideoQuality(
      JITSI_OPTIMAL_VIDEO_CONSTRAINTS_GROUP,
      JITSI_DEFAULT_RESOLUTION_GROUP
    );
  }
};

export const handleTrackMuteStatusChange = ({ participantId, track, isMuted }: any) => {
  if (!track?.track) return;
  const mediaTrackId = track.track.id;
  const isScreenShare = isVideoTrack(track) && track.isScreenSharing && track.isScreenSharing();

  useMeetingStore.setState((state) => {
    const participant = state.jitsiParticipants[participantId];
    if (!participant) return state;

    const updatedParticipant = { ...participant };

    if (isScreenShare && updatedParticipant.screenTrack?.track.track.id === mediaTrackId) {
      updatedParticipant.screenTrack = {
        ...updatedParticipant.screenTrack!,
        isMuted,
      };
    } else if (
      isAudioTrack(track) &&
      updatedParticipant.audioTrack?.track.track.id === mediaTrackId
    ) {
      updatedParticipant.audioTrack = {
        ...updatedParticipant.audioTrack!,
        isMuted,
      };
    } else if (
      isVideoTrack(track) &&
      updatedParticipant.videoTrack?.track.track.id === mediaTrackId
    ) {
      updatedParticipant.videoTrack = {
        ...updatedParticipant.videoTrack!,
        isMuted,
      };
    } else {
      return state;
    }

    return {
      jitsiParticipants: {
        ...state.jitsiParticipants,
        [participantId]: updatedParticipant,
      },
    };
  });
};

export const handleUserLeft = (participantId?: string, participant?: any) => {
  if (!participantId) return;

  useMeetingStore.getState().unregisterParticipantForOrder(participantId);

  useMeetingStore.setState((state) => {
    const { [participantId]: _, ...rest } = state.jitsiParticipants;
    return { jitsiParticipants: rest };
  });

  const userPid = participant._identity?.user?.id;
  if (userPid) {
    reassignRecordingModerator(userPid);
    reassignTranscriptionModerator(userPid);
  }

  // Upgrade resolution when number of people decrease in a meeting
  const meetingSize = Object.keys(useMeetingStore.getState().jitsiParticipants).length;
  if (meetingSize < 6 && jitsiService?.currentResolution !== JITSI_DEFAULT_RESOLUTION) {
    jitsiService!.upgradeVideoQuality(JITSI_OPTIMAL_VIDEO_CONSTRAINTS, JITSI_DEFAULT_RESOLUTION);
  }
};

export const handleIncomingMessage = async (participantId?: string, message?: any) => {
  const { showSnackbar } = useNotificationStore.getState();

  if (!participantId || !message) return;

  switch (message.mode) {
    case 'whisper-signalling-request': {
      if (useMeetingStore.getState().whisperMeetingInfo) {
        getJitsiWhisperService().rejectWhisperSignalling(message);
        break;
      } else {
        useMeetingStore.getState().setWhisperMeetingInfo(message);
        getJitsiWhisperService().acknowledgeWhisperSignalling(
          useMeetingStore.getState().whisperMeetingInfo
        );
        getJitsiWhisperService().joinWhisperingRoom(message);
        break;
      }
    }

    case 'whisper-signalling-ack': {
      useMeetingStore.getState().setWhisperMeetingInfo(message);
      getJitsiWhisperService().joinWhisperingRoom(message);
      break;
    }

    case 'whisper-signalling-reject': {
      showSnackbar('Participant is busy, try whispering later', 'warning');
      break;
    }

    default:
      break;
  }
};

// Synchronous reentrancy lock for joinJitsiMeeting.
//
// A state-based guard (meetingRoomState === JOINING) is NOT sufficient: the room state
// only flips to JOINING deep inside jitsiService.joinMeeting(), which runs AFTER the async
// token fetch in joinJitsiMeeting. During that network round-trip a second caller (e.g.
// GuestLobbyPage's effect firing again on a guestPresence/lobbyDetails change) would sail
// past the state guard and start a second concurrent join. Two concurrent joins create two
// conference objects on the same connection; a local track gets added to one while the
// other becomes `this.conference`, leaving the track with no transceiver — the next
// disposeTracks() then throws "Replace track failed - no transceiver". A synchronous flag
// set before the first await closes that window.
//
// It also marks "joinJitsiMeeting owns the connection lifecycle right now", so the reactive
// CONNECTION_DISCONNECTED handler below defers its leaveMeeting() cleanup while a join is
// establishing/re-initializing the connection.
let joinInProgress = false;

// When a room switch is requested while a previous join is still in flight,
// joinJitsiMeeting() can't start a second concurrent join (see joinInProgress above), so it
// stashes the latest request here instead of dropping it. Once the in-flight join settles,
// this queued request is chased immediately — otherwise the user would silently stay stuck
// in the superseded room, and meetingJoinStatus would flip to 'joined'/'failed' for a room
// nobody asked for anymore instead of staying 'connecting' (which is what keeps the
// participant skeleton visible) for the room actually being switched to.
let pendingJoinRequest: {
  roomIdString: string;
  options?: {
    isBreakoutRoom?: boolean;
    auditorium?: boolean;
    auditoriumSessionPid?: string;
  };
} | null = null;

// Bumped every time a join starts, and again whenever a DIFFERENT room is requested while
// one is still in flight. Each running join captures its own value and compares after every
// await; once it no longer matches, that join is superseded and must stop immediately.
//
// Without this, a mid-join room switch stranded the user: setCurrentRoomId leaves the old
// room first, and for a normal (non-breakout) switch that leave kills the XMPP connection
// (jitsi-meeting.service leaveMeeting, killConnection path). The superseded join then kept
// running against a dead connection through waitForConnection(30s) + waitForRoomJoined(30s),
// so `joinInProgress` stayed true — and the queued switch, which is only chased from this
// function's finally block, didn't start for up to a minute. By then the participant grid's
// skeleton window (roomJoinStartedAt) had long expired, leaving an empty grid with no video
// tiles. It also clobbered store state on the way out (currentRoomId/meetingRoomState for
// the abandoned room). Bailing out on supersession drops the dead attempt within ~100ms and
// lets the queued room start joining right away (it re-initializes a fresh connection,
// since isConnected() is false after the teardown).
let joinGeneration = 0;

// Recurring self-heal for jitsiParticipants vs. the conference's actual roster (see
// reconcileParticipants below). Module-scoped so CONFERENCE_LEFT can clear it and a
// later CONFERENCE_JOINED doesn't stack a second interval on top of an existing one.
let participantReconcileInterval: ReturnType<typeof setInterval> | undefined;

export const setupEventListeners = () => {
  if (!jitsiService) return;

  return jitsiService.events$.subscribe((event) => {
    // Connection success is now handled explicitly in joinJitsiMeeting with waitForConnection
    // Removed automatic join to prevent race condition

    if (isJitsiConnectionDisconnectedEvent(event.type)) {
      // Don't tear down the connection while joinJitsiMeeting is actively establishing
      // it. A transient CONNECTION_DISCONNECTED during connect() would otherwise fire
      // leaveMeeting() → null this.connection → waitForConnection times out → the retry
      // throws "Connection not initialized" and the meeting never renders. While a join
      // is in flight, joinJitsiMeeting owns the connection lifecycle (it re-initializes
      // on timeout), so the reactive cleanup must defer to it.
      const roomStateOnDisconnect = jitsiService?.getRoomState?.();
      if (joinInProgress) {
        // joinJitsiMeeting owns the connection lifecycle while a join is in flight (it
        // re-initializes on timeout); a transient disconnect during connect() must not
        // tear the connection down here or waitForConnection would time out.
      } else if (roomStateOnDisconnect === MeetingRoomState.JOINED) {
        // A disconnect arrived while we are actively JOINED to a conference. This is almost
        // always a DELAYED CONNECTION_DISCONNECTED from a SUPERSEDED connection (e.g. a
        // previous room's connection firing its disconnect via the safety-timeout). Running
        // leaveMeeting() now would tear down the healthy current conference. Ignore it.
      } else {
        jitsiService?.leaveMeeting();
      }
    }

    if (event.type === JitsiConferenceEvents.CONFERENCE_LEFT) {
      clearInterval(participantReconcileInterval);
      participantReconcileInterval = undefined;
    }

    if (
      event.type === JitsiConferenceEvents.CONFERENCE_JOINED ||
      event.type === JitsiConferenceEvents.CONFERENCE_LEFT
    ) {
      useMeetingStore.setState({ meetingRoomState: jitsiService?.getRoomState() });
    }

    if (event.type === JitsiConferenceEvents.CONFERENCE_JOINED) {
      useMeetingStore.getState().setLocalUserJoinedAt(Date.now());
      useMeetingStore.getState().resetParticipantSpeaking();

      // Seed the local participant into the store immediately so their tile is
      // visible even when they have no camera and no microphone (no TRACK_ADDED
      // events would ever fire in that case).
      const myId = jitsiService?.getMyUserId();
      if (myId) {
        useMeetingStore.setState((state) => {
          const user = useWorkplaceStore.getState().user;
          const userPid = user?.pid;

          // Purge any ghost entries left over from a previous connection
          // (page refresh, network blip + reconnect, etc.). Two cases:
          // 1. Stale local entry with old Jitsi ID (isLocal: true)
          // 2. Ghost remote entry added by TRACK_ADDED before CONFERENCE_JOINED,
          //    which has isLocal: false but shares the local user's userPid.
          //
          // This runs even when an entry for myId already exists. It used to bail out
          // first, which broke the room-switch case: switchToRoom deliberately carries the
          // local tile over under the PREVIOUS conference's id, and a local TRACK_ADDED
          // then creates a second entry under the new id before CONFERENCE_JOINED arrives.
          // The bail-out saw that new entry, skipped the purge, and left two isLocal tiles
          // standing — the stale one resolving isSelf to false (mic button disabled) and
          // sending toggleTrack down the remote-mute path (camera never toggles).
          let participants = { ...state.jitsiParticipants };
          const ghostIds = Object.keys(participants).filter(
            (id) =>
              id !== myId &&
              (participants[id].isLocal || (userPid && participants[id].userPid === userPid))
          );
          if (ghostIds.length > 0) {
            Logger.info('[Jitsi] Purging ghost participants on CONFERENCE_JOINED:', ghostIds);
            for (const ghostId of ghostIds) {
              const ghost = participants[ghostId];
              [ghost?.audioTrack, ghost?.videoTrack, ghost?.screenTrack].forEach((t) => {
                if (t?.track) {
                  try {
                    t.track.detach?.();
                  } catch (e) {
                    Logger.error('[Jitsi] Error detaching ghost track', e);
                  }
                  try {
                    t.track.dispose?.();
                  } catch (e) {
                    Logger.error('[Jitsi] Error disposing ghost track', e);
                  }
                }
              });
              const { [ghostId]: _, ...rest } = participants;
              participants = rest;
            }
          }

          // Already seeded by a local TRACK_ADDED — keep that entry (it owns the track
          // slots) and just publish the purge.
          if (participants[myId]) {
            return ghostIds.length > 0 ? { jitsiParticipants: participants } : state;
          }

          const guest = useGuestStore.getState().visitDetails?.guest;
          const guestAvatarUrl = guest?.avatar_url;
          const guestAvatarThumbnailUrl = guest?.avatar_thumbnail_url;
          // NGE-681: the local user's auditorium role is known authoritatively, so
          // seed isModerator from it (audience → false, moderator/speaker → true).
          const auditoriumRole = state.auditoriumRole;
          const localIsModerator = auditoriumRole ? auditoriumRole !== 'audience' : undefined;
          return {
            jitsiParticipants: {
              ...participants,
              [myId]: {
                id: myId,
                displayName: user?.name ?? 'Me',
                isLocal: true,
                userPid: user?.pid,
                avatarUrl: user?.avatar_url || guestAvatarUrl || '',
                avatarThumbnailUrl:
                  user?.avatar_thumbnail_url || guestAvatarThumbnailUrl || undefined,
                isModerator: localIsModerator,
              },
            },
          };
        });
        useMeetingStore.getState().registerParticipantForOrder(myId);
      }

      // Self-heal the tile grid against Jitsi's own roster instead of trusting that
      // every USER_JOINED/USER_LEFT/TRACK_ADDED fired in order. Users have reported
      // duplicate tiles, "Unknown"-named tiles, and missing tiles — all symptoms of
      // the event-derived store drifting from the conference's actual participant
      // list (most likely during a breakout room switch race, or a refresh landing
      // mid-reconnect for some other participant). A one-shot pass anchored to this
      // client's own CONFERENCE_JOINED isn't enough — if a peer's own presence data
      // (name/avatar) resolves later than that window, their tile is stuck on
      // "Unknown" for the rest of the session. Run continuously while in this
      // conference instead, so any peer's late-arriving presence still gets picked up.
      const reconcileParticipants = () => {
        const conferenceIds = new Set([myId, ...(jitsiService?.getParticipantIds() ?? [])]);

        useMeetingStore.setState((state) => {
          let changed = false;
          const next = { ...state.jitsiParticipants };

          // Drop anything not actually in the conference (duplicates/ghosts/stale entries).
          for (const id of Object.keys(next)) {
            if (!conferenceIds.has(id)) {
              delete next[id];
              queueMicrotask(() => useMeetingStore.getState().unregisterParticipantForOrder(id));
              changed = true;
            }
          }

          // Keep the local participant's avatar in sync with the workplace store.
          // The entry is seeded at conference join — before bootstrap/self-heal may
          // have populated avatar_thumbnail_url — so refresh it on every pass.
          const localParticipantEntry = next[myId];
          const wpUser = useWorkplaceStore.getState().user;
          if (localParticipantEntry) {
            const localAvatar = wpUser.avatar_url || localParticipantEntry.avatarUrl;
            const localThumb = wpUser.avatar_thumbnail_url ?? undefined;
            if (
              localParticipantEntry.avatarUrl !== localAvatar ||
              localParticipantEntry.avatarThumbnailUrl !== localThumb
            ) {
              next[myId] = {
                ...localParticipantEntry,
                avatarUrl: localAvatar,
                avatarThumbnailUrl: localThumb,
              };
              changed = true;
            }
          }

          // Add/hydrate anyone the conference knows about but the store is missing or
          // still shows as "Unknown" — covers a USER_JOINED that never fired or fired
          // before display-name presence data had resolved.
          for (const id of conferenceIds) {
            if (!id || id === myId) continue;
            const existing = next[id];
            if (existing && existing.displayName !== 'Unknown') continue;

            const truth = jitsiService?.getParticipantById(id);
            if (!truth) continue;

            next[id] = {
              ...existing,
              id,
              displayName: truth.displayName ?? existing?.displayName ?? 'Unknown',
              isLocal: false,
              userPid: truth.userPid ?? existing?.userPid,
              avatarUrl: (truth as any).avatarUrl ?? (truth as any).avatar ?? existing?.avatarUrl,
              avatarThumbnailUrl:
                (truth as any).avatarThumbnailUrl ??
                (truth as any).avatar_thumbnail ??
                existing?.avatarThumbnailUrl,
            };
            changed = true;
          }

          // Repair the LOCAL video slot against the conference's actual local tracks.
          //
          // The passes above heal roster drift but never touch tracks, so a local camera
          // track that fails to land in the store has no recovery path at all — the tile
          // shows the avatar for the rest of the session while the camera light stays on.
          // That is reachable several ways on a room switch (a replaceTrack removal
          // clearing the slot, a TRACK_ADDED lost while the subscription was being rebuilt,
          // or a stale pre-switch track left in the slot with its stream already stopped),
          // and they are indistinguishable from here — so reconcile against the one
          // authority that is always right: what the conference is actually publishing.
          const localEntry = next[myId];
          if (localEntry) {
            const liveCamera = (jitsiService as any)?.conference
              ?.getLocalTracks?.()
              ?.find(
                (t: any) =>
                  t?.getType?.() === 'video' &&
                  t?.videoType !== 'desktop' &&
                  !t?.isMuted?.() &&
                  t?.getTrack?.()?.readyState === 'live'
              );

            if (liveCamera && localEntry.videoTrack?.track !== liveCamera) {
              next[myId] = {
                ...localEntry,
                videoTrack: {
                  id: liveCamera.stream?.id ?? liveCamera.getId?.(),
                  type: 'video',
                  track: liveCamera,
                },
              };
              changed = true;
              Logger.info('[Jitsi] Reconciled local video track into the store for', myId);
            }
          }

          return changed ? { jitsiParticipants: next } : state;
        });
      };

      reconcileParticipants();
      setTimeout(reconcileParticipants, 1500);
      clearInterval(participantReconcileInterval);
      participantReconcileInterval = setInterval(reconcileParticipants, 5000);

      // Re-sync floor presence so the floor map avatar position matches the room
      // just joined. Guards against missed WebSocket events during navigation.
      const currentFloorPid = useMultiFloorStateStore.getState().currentFloorPresence;
      if (currentFloorPid) {
        OfficeService.getInstance()
          .fetchFloorPresence(currentFloorPid)
          .catch((err) =>
            Logger.warn('[Meeting] Failed to sync floor presence on CONFERENCE_JOINED:', err)
          );
      }
    }

    if (event.type === JitsiConferenceEvents.TRACK_ADDED) {
      handleTrackAdded(event.payload);
    }

    if (event.type === JitsiConferenceEvents.TRACK_REMOVED) {
      handleTrackRemoved(event.payload);
    }

    if (event.type == JitsiConferenceEvents.TRACK_MUTE_CHANGED) {
      if (isScreenTrack(event.payload)) {
        if (event.payload.isMuted()) {
          handleTrackRemoved(event.payload);
        } else {
          handleTrackAdded(event.payload);
        }
      } else {
        handleTrackMuteStatusChange({
          isMuted: (event as any).isMuted,
          track: event.payload,
          participantId: event.payload.getParticipantId(),
        });
      }
    }

    if (event.type === JitsiConferenceEvents.USER_JOINED) {
      handleUserJoined(event.participantId, event.payload);
    }

    if (event.type === JitsiConferenceEvents.USER_LEFT) {
      handleUserLeft(event.participantId, event.payload);
    }

    if (event.type === JitsiConferenceEvents.PRIVATE_MESSAGE_RECEIVED) {
      handleIncomingMessage(event.participantId, event.payload);
    }

    if (event.type === JitsiGenericEvent.AUDIO_MUTED) {
      handleTrackMuteStatusChange({ ...event, isMuted: !event.type.includes('unmuted') });
    }

    if (event.type === JitsiGenericEvent.AUDIO_UNMUTED) {
      handleTrackMuteStatusChange({ ...event, isMuted: !event.type.includes('unmuted') });
    }
    if (event.type === JitsiGenericEvent.VIDEO_MUTED) {
      handleTrackMuteStatusChange({ ...event, isMuted: !event.type.includes('unmuted') });
    }
    if (event.type === JitsiGenericEvent.VIDEO_UNMUTED) {
      handleTrackMuteStatusChange({ ...event, isMuted: !event.type.includes('unmuted') });
    }

    if (event.type === JitsiConferenceEvents.RECORDER_STATE_CHANGED) {
      const data = event.payload;
      if (!data?._statusFromJicofo) return; // Jicofo can fire this before status is ready
      MeetingService.instance.loadRoomRecordingInfo(data?._sessionID);
      useRecordingStore.getState().setActiveRecordingSession(data);
      useMeetingStore.getState().setRecordingSessionId(data._sessionID);
    }

    if (event.type === JitsiConferenceEvents.BREAKOUT_ROOMS_UPDATED) {
      useBreakoutStore.getState().setBreakoutRooms(event.payload.rooms);
    }

    if (event.type === JitsiConferenceEvents.BREAKOUT_ROOMS_MOVE_TO_ROOM) {
      // Intentionally a no-op. This app manages breakout room membership entirely
      // through its own backend-driven system (currentBreakoutRoomGroups polling in
      // breakoutroom.store.ts, applied via FloatingMeetingView's switchMyRoom/
      // switchMainRoom calls into JitsiMeetingService.switchToRoom). Jitsi's native
      // breakout-rooms XMPP extension (lib-jitsi-meet's BreakoutRooms.js) ALSO fires
      // this event independently and used to trigger a second, uncoordinated
      // joinJitsiMeeting() call for the same room transition. When it raced the
      // custom system's own switch, initJitsiConference() threw "A conference with
      // the same name has already been created!", and the failure's retry path
      // crashed with "Cannot read properties of null (reading 'join')" — leaving
      // the participant fully disconnected (the "Couldn't connect" tile). Do not
      // re-enable this without also removing/reconciling the custom switch system,
      // or the two will keep fighting over the same room transitions.
      Logger.debug(
        '[Meeting] Ignoring native BREAKOUT_ROOMS_MOVE_TO_ROOM — handled by custom breakout system:',
        event.payload
      );
    }

    if (event.type === JitsiGenericEvent.LOCAL_TRACKS_ERROR) {
      // NGE-681: auditorium audience join receive-only and never publish, so a
      // camera/mic init failure is expected and must not surface a scary error.
      const { isAuditorium, auditoriumRole } = useMeetingStore.getState();
      if (!(isAuditorium && auditoriumRole === 'audience')) {
        const { showSnackbar } = useNotificationStore.getState();
        showSnackbar(
          'Could not start your camera or microphone. Please check your device permissions and try rejoining.',
          'error'
        );
      }
    }
  });
};

// Replicates the validateAlwaysVideo logic from usePoliciesApply for non-React contexts.
// Must stay in sync with the hierarchy in usePoliciesApply: floor overrides org, then exceptions.
function isAlwaysVideoActive(): boolean {
  const { policies, policiesLoadedInSession } = useAdministrationStore.getState();
  if (!policiesLoadedInSession) return false;
  // Same guest exemption as usePoliciesApply.validateAlwaysVideo — guests always join with
  // the camera on, but stay free to turn it off themselves.
  if (isGuestExemptFromAlwaysVideo()) return false;
  const floorPid = useMultiFloorStateStore.getState().currentFloorPresence;
  const userPid = useWorkplaceStore.getState().user?.pid;

  const key = PolicyTypeEnum.ALWAYS_VIDEO;
  const floorPolicy = floorPid
    ? policies.find((p) => p.policy.key === key && p.scope === 'floor' && p.scope_pid === floorPid)
    : undefined;
  const orgPolicy = policies.find((p) => p.policy.key === key && p.scope === 'organization');
  const effective = floorPolicy ?? orgPolicy;

  if (!effective?.active) return false;
  if (userPid && effective.exceptions?.users?.includes(userPid)) return false;
  return true;
}

/**
 * NGE-1078: whether the room being joined is an auditorium, decided by the room's
 * own pid rather than by whichever room the viewer happens to be in.
 *
 * `currentRoom` is the wrong thing to ask: it is the room being left as often as the
 * one being joined (and it lags behind a presence-driven join), so a check on its
 * type alone answered "auditorium" for a normal room the viewer was switching to,
 * and "no" for the auditorium they were walking into.
 */
function isAuditoriumRoomPid(roomPid: string): boolean {
  const floors = useMultiFloorStateStore.getState().floors;
  for (const floor of Object.values(floors)) {
    const room = floor?.place?.rooms?.find((candidate) => candidate.pid === roomPid);
    if (room) return room.type === 'auditorium';
  }

  // The floor's place isn't loaded for this room — the only other thing that knows
  // its type is the room the viewer is standing in.
  const currentRoom = useWorkplaceStore.getState().currentRoom;
  return currentRoom?.pid === roomPid && currentRoom.type === 'auditorium';
}

/**
 * NGE-1078: whether this is someone walking into an auditorium room with no session
 * named. Walking in used to call the get-or-create endpoint, which started an ad-hoc
 * live session and pulled the visitor into a conference — so an auditorium was always
 * "busy" the moment anyone stood in it, and the bookings made for it could not be
 * started. Entry now only opens the sessions panel; joining is an explicit act (the
 * panel's Join or Start, or a share link), and each of those names its session.
 */
function isAuditoriumEntryWithoutSession(
  roomIdString: string,
  options?: { isBreakoutRoom?: boolean; auditorium?: boolean; auditoriumSessionPid?: string }
): boolean {
  if (options?.isBreakoutRoom || options?.auditoriumSessionPid) return false;

  // Guests always join. A share-link guest came to attend the session, and the
  // sessions panel — the thing entry opens instead — is a member's tool they never
  // see, so skipping the join would leave them staring at an empty room.
  if (localStorage.getItem('isUserGuest') === 'true') return false;

  if (options?.auditorium === true) return true;
  return isAuditoriumRoomPid(roomIdString);
}

export async function joinJitsiMeeting(
  roomIdString: string,
  options?: {
    isBreakoutRoom?: boolean;
    // NGE-681: force auditorium mode (used by the guest live page, which has no
    // workplace currentRoom) and optionally pass a known session pid (guests can't
    // call the member-only get-or-create endpoint).
    auditorium?: boolean;
    auditoriumSessionPid?: string;
  }
) {
  // Checked before any join state is written: this path joins nothing, so it must
  // not leave a 'connecting' status or a retry tile behind either.
  if (isAuditoriumEntryWithoutSession(roomIdString, options)) {
    Logger.debug('[joinJitsiMeeting] 🎭 Auditorium entry without a session — not joining');
    useMeetingStore.getState().setAuditoriumMode(false);
    stopAuditoriumRoleSync();
    useMeetingStore.setState({ meetingJoinStatus: 'idle', lastJoinRequest: null });
    return;
  }

  if (joinInProgress) {
    // Only queue this as a follow-up if it's actually for a different room than the one
    // already being joined — a re-entrant call for the SAME room (e.g. a duplicate effect
    // firing again) must be a no-op. Queuing it unconditionally meant that once the real
    // in-flight join for this room completed, `pendingJoinRequest` was still non-null, so
    // the success branch below skipped writing 'joined' and instead re-ran the whole join
    // from scratch — flipping status back to 'connecting' and leaving the skeleton stuck
    // even though the conference was already fully joined.
    const inFlightRoomId = useMeetingStore.getState().lastJoinRequest?.roomId;
    if (roomIdString === inFlightRoomId) {
      Logger.debug(
        '[joinJitsiMeeting] ⏭️ Join already in progress for this room, ignoring duplicate call'
      );
      return;
    }
    Logger.debug(
      '[joinJitsiMeeting] ⏭️ Join already in progress, queuing latest request for room:',
      roomIdString
    );
    pendingJoinRequest = { roomIdString, options };
    // Supersede the in-flight join for the room we've moved on from. Queuing alone isn't
    // enough: the running attempt captured its own generation and only aborts when this
    // counter changes, so without the bump it kept polling waitForConnection/waitForRoomJoined
    // for the full 30s + 30s on a connection this switch already tore down — holding
    // joinInProgress true and delaying the queued room (chased from the finally below) by up
    // to a minute, long past the participant grid's skeleton window.
    joinGeneration++;
    return;
  }
  joinInProgress = true;
  const myGeneration = ++joinGeneration;
  const isSuperseded = () => joinGeneration !== myGeneration;
  // Record the request so retryJoinMeeting() can replay it, and surface a "connecting"
  // state so the meeting view can show a loading tile while we connect/join.
  //
  // roomJoinStartedAt is (re)stamped here because this is where an attempt actually begins —
  // including a queued one chased from the finally block below. setCurrentRoomId stamps it
  // too, but only once per user action: across rapid consecutive switches the participant
  // grid's skeleton window would otherwise still be counting from the FIRST switch of the
  // chain and could expire while the join for the room we actually want is still running,
  // leaving an empty grid. Each attempt restarts the window instead.
  useMeetingStore.setState({
    lastJoinRequest: { roomId: roomIdString, options },
    meetingJoinStatus: 'connecting',
    roomJoinStartedAt: Date.now(),
  });
  try {
    await joinJitsiMeetingImpl(roomIdString, options, isSuperseded);
    // Success is authoritative on the conference reaching JOINED — the same signal the
    // impl uses to sync meetingRoomState. Any other outcome (timeout, token/connection
    // failure, early return) leaves us not-JOINED → 'failed', which drives the retry tile.
    // Skip writing this outcome if a newer room switch is already queued or this attempt was
    // superseded — it's for a room nobody wants anymore and would otherwise flash
    // 'joined'/'failed' before the queued request flips status back to 'connecting'.
    if (!pendingJoinRequest && !isSuperseded()) {
      const joined = jitsiService?.getRoomState?.() === MeetingRoomState.JOINED;
      useMeetingStore.getState().setMeetingJoinStatus(joined ? 'joined' : 'failed');
    }
  } catch (err) {
    if (!pendingJoinRequest && !isSuperseded()) {
      useMeetingStore.getState().setMeetingJoinStatus('failed');
    }
    throw err;
  } finally {
    joinInProgress = false;
    if (pendingJoinRequest) {
      const next = pendingJoinRequest;
      pendingJoinRequest = null;
      // Chase the latest requested room now instead of leaving the user stranded in the
      // room this superseded join was for.
      void joinJitsiMeeting(next.roomIdString, next.options);
    }
  }
}

async function joinJitsiMeetingImpl(
  roomIdString: string,
  // NGE-681: mirror the public joinJitsiMeeting options so the auditorium fields the
  // body reads (options.auditorium / auditoriumSessionPid) are in scope.
  options?: {
    isBreakoutRoom?: boolean;
    auditorium?: boolean;
    auditoriumSessionPid?: string;
  },
  // True once a newer room has been requested for this client. Checked after every await so
  // an abandoned attempt stops touching the connection/conference and stops writing store
  // state for a room the user has already moved on from (see joinGeneration above).
  isSuperseded: () => boolean = () => false
) {
  if (!roomIdString || roomIdString.trim() === '') {
    console.error('[MeetingStoreUtils] ❌ roomIdString is empty, cannot join meeting');
    return;
  }

  Logger.debug('[joinJitsiMeeting] 🚀 STARTING JITSI MEETING JOIN');
  Logger.debug('[joinJitsiMeeting] Room ID String:', roomIdString);
  Logger.debug('[joinJitsiMeeting] Options:', options);

  let roomId;
  let token;

  if (!jitsiService) {
    console.error('[joinJitsiMeeting] ❌ CRITICAL: jitsiService is not initialized!');
    return;
  }

  // NGE-681: auditorium rooms join the same meeting view, but with a role-scoped
  // token (audience = receive-only) resolved from the auditorium endpoints.
  // Guests arrive via a share link with a stashed context (sessionPid + roomPid);
  // detect auditorium for them regardless of currentRoom render timing.
  let guestAuditoriumCtx: { sessionPid?: string; roomPid?: string } | null = null;
  if (localStorage.getItem('isUserGuest') === 'true') {
    try {
      guestAuditoriumCtx = JSON.parse(sessionStorage.getItem('auditoriumContext') || 'null');
    } catch {
      guestAuditoriumCtx = null;
    }
  }
  // A guest who arrived via an auditorium share link only ever attends that
  // auditorium (as receive-only audience). Treat ANY join as the auditorium when the
  // stashed context exists — the auditorium branch joins the real auditorium room
  // (tRes.roomId) regardless of the transient roomIdString the office may pass during
  // guest bootstrap, so they never fall into the normal-meeting branch that prompts
  // for camera/microphone.
  const isGuestAuditorium = !!guestAuditoriumCtx?.roomPid;
  const isAuditoriumRoom =
    options?.auditorium === true ||
    isGuestAuditorium ||
    guestAuditoriumCtx?.roomPid === roomIdString ||
    // Asked of the room being joined, not of the room the viewer is in: the loose
    // check here treated a switch *out* of an auditorium as another auditorium join
    // and booked an ad-hoc session against the room being entered.
    isAuditoriumRoomPid(roomIdString);
  let auditoriumRole: 'moderator' | 'speaker' | 'audience' | null = null;

  if (!options?.isBreakoutRoom && isAuditoriumRoom) {
    const sessionId = useSessionStore.getState().getOrCreateSessionId();
    let auditoriumSessionPid = options?.auditoriumSessionPid || guestAuditoriumCtx?.sessionPid;
    // Members ensure a live session for the room (caller becomes host if new).
    if (!auditoriumSessionPid) {
      const [sErr, session] = await handleAsync(() =>
        ApiService.request<any>({
          endpoint: `auditorium/room/${roomIdString}/session`,
          method: 'POST',
        })
      );
      if (sErr || !session?.pid) {
        console.error('[joinJitsiMeeting] ❌ Failed to start auditorium session:', sErr);
        // Plan denials (403 with `feature`) were already surfaced by ApiService.
        if (!((sErr as any)?.status === 403 && (sErr as any)?.feature)) {
          useNotificationStore
            .getState()
            .showSnackbar(
              (sErr as any)?.status === 403
                ? ENTITLEMENT_UPGRADE_MESSAGE
                : 'Failed to start the auditorium session, please try again.',
              'error'
            );
        }
        return;
      }
      auditoriumSessionPid = session.pid;
    }
    // NGE-1078: an invite-only session accepts a guest only with the token from the
    // link they arrived on; members are resolved by their own identity and ignore it.
    const linkJoinToken = readStoredJoinToken();
    const [tErr, tRes] = await handleAsync(() =>
      ApiService.request<any>({
        endpoint: `auditorium/${auditoriumSessionPid}/token`,
        data: {
          session_id: sessionId,
          ...(linkJoinToken ? { join_token: linkJoinToken } : {}),
        },
      })
    );
    if (tErr || !tRes?.token) {
      console.error('[joinJitsiMeeting] ❌ Failed to get auditorium token:', tErr);
      return;
    }
    if (isSuperseded()) return;
    auditoriumRole = tRes.role;
    roomId = tRes.roomId;
    token = tRes.token;
    useMeetingStore.getState().setAuditoriumMode(
      true,
      tRes.role,
      auditoriumSessionPid ?? null,
      tRes.session?.chat_room_id ?? null,
      !!tRes.session?.chat_locked,
      // Who may end this session, which the role cannot answer: every member with
      // no explicit role row joins as `moderator` so they can present.
      !!tRes.can_moderate,
      // Who may moderate the room. Undefined from a server that predates the flag —
      // passed through as null so the UI falls back to the role instead of hiding the
      // controls from a real moderator.
      tRes.can_manage_room === undefined ? null : !!tRes.can_manage_room
    );
    // Room Chat (NGE-804): land the joining user straight in the auditorium's
    // chat instead of making them hunt for it in the sidebar. Registered
    // optimistically (same pattern as ensureMeetingChatRoom) rather than
    // waiting on the DDP subscriptions-changed push to relay it back.
    if (tRes.session?.chat_room_id) {
      // Matches the backend's own sanitization (AuditoriumService.buildChatRoomName)
      // so this optimistic placeholder already shows the real channel name —
      // it gets overwritten anyway once the DDP/subscriptions sync lands, but
      // avoids a flash of a session-pid-based name in the meantime.
      const sanitizedName = (tRes.session.title ?? '')
        .split(/[^a-zA-Z0-9]+/)
        .filter(Boolean)
        .join('-');
      useChatPanelStore.getState().upsertRoom({
        _id: tRes.session.chat_room_id,
        t: 'c',
        name: sanitizedName || `Auditorium-${auditoriumSessionPid}`,
        fname: tRes.session.title || undefined,
        unread: 0,
        customFields: {
          nexgenAuditoriumChat: true,
          auditoriumSessionPid: auditoriumSessionPid ?? undefined,
        },
        ro: !!tRes.session.chat_locked,
      });
      useChatPanelStore.getState().setActiveRoom(tRes.session.chat_room_id);
      setChatOpenSource('auditorium_join');
      ensureChatDrawerOpen();
    }
    useMeetingStore.setState({ currentRoomToken: token });
  } else if (!options?.isBreakoutRoom) {
    useMeetingStore.getState().setAuditoriumMode(false);
    stopAuditoriumRoleSync();
    Logger.debug('[joinJitsiMeeting] 📞 Requesting token for room:', roomIdString);
    Logger.debug(
      '[joinJitsiMeeting] 👤 User is guest:',
      localStorage.getItem('guestToken') !== null
    );

    const sessionId = useSessionStore.getState().getOrCreateSessionId();
    const [err, result] = await handleAsync(() =>
      ApiService.request<any>({
        endpoint: `meeting/${roomIdString}/token`,
        data: { session_id: sessionId },
      })
    );

    if (err || !result) {
      console.error('[joinJitsiMeeting] ❌ Error getting meeting token:', err);
      return;
    }

    if (!result.token) {
      console.error(
        '[joinJitsiMeeting] ❌ No token returned from meeting endpoint. Result:',
        result
      );
      return;
    }

    Logger.debug('[joinJitsiMeeting] ✅ Token received successfully');
    Logger.debug('[joinJitsiMeeting] Room ID:', result.roomId);

    // The token round-trip is the first real await of a join, so it is where a rapid switch
    // most often lands. Writing currentRoomToken/currentRoomId below for a superseded room
    // hands the newer join a token for the room the user just left.
    if (isSuperseded()) return;

    useMeetingStore.setState((state) => ({ ...state, currentRoomToken: result.token }));
    roomId = result.roomId;
    token = result.token;
  } else {
    roomId = roomIdString;
    token = useMeetingStore.getState().currentRoomToken ?? '';
    Logger.debug('[joinJitsiMeeting] Reusing token for breakout room');
  }

  if (isSuperseded()) return;

  useMeetingStore.setState({ currentRoomId: roomId, currentRoomToken: token });

  const currentSub = useMeetingStore.getState().subscription;

  if (!currentSub) {
    useMeetingStore.setState({ subscription: setupEventListeners() });
  }

  const isConnected = jitsiService.isConnected();

  // Capture state before join: forceVideoOn ensures the video track is created
  // immediately during initLocalTracks instead of being added post-join, which
  // eliminated the visible delay in the video tile appearing.
  // A guest who took a photo on the device-check screen must arrive in the lobby with the
  // camera up. forceVideoOn guarantees the video track is created during initLocalTracks
  // instead of relying only on the persisted mute state, which the audio-only fallback can
  // flip to muted when the camera is still settling from the previous screen. Consumed once,
  // so a camera the guest later turns off themselves stays off on the next room change.
  const forceVideoOn = isAlwaysVideoActive() || consumeGuestVideoOptIn();
  const cameraWasOff = jitsiService?.getLocalMuteState?.()?.isVideoMuted ?? false;

  // Everyone in an auditorium gets local tracks, audience included: a guest holds a
  // microphone and camera of their own and only screen share is kept from them. They
  // still arrive muted — the start-muted policy sees to that — so the tracks exist to
  // be turned on rather than being live on arrival.
  const joinOpts = {
    token,
    forceVideoOn,
    ...(isAuditoriumRoom ? { createLocalTracks: true } : {}),
    // Let the service abandon the join if a newer switch lands while it is queued behind
    // another operation or retrying conference.join() — neither of which we can interrupt
    // from out here, since joinMeeting() is a single un-awaited-in-pieces call.
    shouldAbort: isSuperseded,
  };

  if (isConnected) {
    Logger.debug('[joinJitsiMeeting] ✅ Already connected, joining meeting directly');
    await jitsiService.joinMeeting(roomId, joinOpts);
  } else {
    Logger.debug('[joinJitsiMeeting] 🔌 Not connected, initializing connection first');
    await jitsiService.initialize({ roomConfig: { roomId } }, token);
    // initialize() opens a fresh XMPP connection for THIS room. If we've been superseded,
    // stop here: the newer join re-initializes its own connection, and continuing would
    // race two connection setups against each other.
    if (isSuperseded()) return;

    // Wait for connection to be established before joining.
    //
    // isSuperseded must be threaded in here, not only into the post-timeout retry below:
    // this is the wait the normal path actually takes, and a room switch tears down the very
    // connection it is polling for. Without the abort the superseded attempt sat here for the
    // full 30s, holding joinInProgress true — so the queued room (chased only from
    // joinJitsiMeeting's finally) didn't start until long after the participant grid's
    // skeleton window had expired.
    let connectionEstablished = await waitForConnection(jitsiService, 30000, isSuperseded);

    if (isSuperseded()) return;

    if (!connectionEstablished) {
      // The connection failed to establish (e.g. a BridgeChannel error or a transient
      // disconnect tore it down mid-setup). Retrying joinMeeting() here is futile — the
      // connection is null/dead, so it throws "Connection not initialized". Instead tear
      // down the dead connection and re-initialize a fresh one before retrying.
      Logger.debug('[joinJitsiMeeting] ⏳ Connection timeout — re-initializing connection');
      try {
        await jitsiService.leaveMeeting({ killConnection: true });
      } catch (cleanupErr) {
        console.warn('[joinJitsiMeeting] cleanup before re-init failed:', cleanupErr);
      }
      if (isSuperseded()) return;
      await jitsiService.initialize({ roomConfig: { roomId } }, token);
      connectionEstablished = await waitForConnection(jitsiService, 30000, isSuperseded);
      if (isSuperseded()) return;
    }

    if (connectionEstablished) {
      Logger.debug('[joinJitsiMeeting] ✅ Connection established, now joining meeting');
      await jitsiService.joinMeeting(roomId, joinOpts);
    } else {
      console.error('[joinJitsiMeeting] ❌ Connection could not be established after re-init');
    }
  }

  // Authoritatively sync the store's meetingRoomState from the conference once it reaches
  // JOINED. The subscription-based update (CONFERENCE_JOINED → meetingRoomState) can be
  // missed during summon churn: leaveJitsiMeeting tears the subscription down and joinJitsi-
  // Meeting recreates it, and the CONFERENCE_JOINED event can fire in that gap (or be skipped
  // when joinMeeting finds the conference already present). When that happens the guest's
  // media joins — the host sees the guest's tile — but the guest's own meetingRoomState
  // stays stale, isInMeeting is false, and the ParticipantsGrid never renders. Polling
  // getRoomState() here closes that gap regardless of subscription timing.
  await waitForRoomJoined(jitsiService, 30000, isSuperseded);
  // Everything below reports the outcome of THIS attempt — room state, auditorium role
  // enforcement, the always-on-video notice. A superseded attempt must publish none of it, or
  // it overwrites the newer room's meetingRoomState and applies the old room's auditorium
  // moderation to the conference the user is actually in.
  if (isSuperseded()) return;
  const roomStateAfterJoin = jitsiService.getRoomState();
  if (useMeetingStore.getState().meetingRoomState !== roomStateAfterJoin) {
    useMeetingStore.setState({ meetingRoomState: roomStateAfterJoin });
  }

  // NGE-681: auditorium post-join. A moderator sets the start-muted policy for the
  // conference; everyone asks the bridge for the streams the stage needs, since every
  // attendee has speaker cameras and a screen share to receive — not just the ones who
  // cannot publish.
  if (isAuditoriumRoom) {
    if (auditoriumRole === 'moderator') {
      await jitsiService.enforceStartMuted?.();
    }
    await jitsiService.configureReceiveOnly?.();
  }

  // NGE-1078: watch for a promotion/demotion of ourselves — the role lives in the
  // join token, so a change means re-fetching it and reconnecting.
  const joinedAuditoriumSessionPid = useMeetingStore.getState().auditoriumSessionPid;
  if (isAuditoriumRoom && joinedAuditoriumSessionPid) {
    startAuditoriumRoleSync(joinedAuditoriumSessionPid);
  }

  if (forceVideoOn && cameraWasOff) {
    useNotificationStore.getState().notify({
      title: 'Camera enabled by policy',
      message: 'Your camera has been turned on as required by your organisation policy.',
      severity: 'info',
    });
  }
}

/**
 * Wait for the Jitsi conference to reach the JOINED room state. CONFERENCE_JOINED fires
 * asynchronously after conference.join() resolves, so poll the authoritative room state.
 * Returns true if JOINED within the timeout, false otherwise.
 */
async function waitForRoomJoined(
  service: any,
  timeout: number = 30000,
  // Abort early when a newer room switch has taken over: without this the caller would sit
  // here for the full 30s on a conference that is never going to join, and the queued switch
  // (chased only after joinJitsiMeeting's finally) would not start until then.
  isSuperseded: () => boolean = () => false
): Promise<boolean> {
  const startTime = Date.now();
  const checkInterval = 100;

  while (Date.now() - startTime < timeout) {
    if (service?.getRoomState?.() === MeetingRoomState.JOINED) {
      return true;
    }
    if (isSuperseded()) return false;
    await new Promise((resolve) => setTimeout(resolve, checkInterval));
  }

  return service?.getRoomState?.() === MeetingRoomState.JOINED;
}

/**
 * Wait for Jitsi connection to be established
 * @param service - JitsiMeetingService instance
 * @param timeout - Maximum time to wait in milliseconds
 * @returns Promise that resolves to true if connected, false if timeout
 */
async function waitForConnection(
  service: any,
  timeout: number = 30000,
  // See waitForRoomJoined: a room switch kills the connection this loop is waiting on, so
  // without an abort it burns the full 30s before the queued switch can start.
  isSuperseded: () => boolean = () => false
): Promise<boolean> {
  const startTime = Date.now();
  const checkInterval = 100; // Check every 100ms

  while (Date.now() - startTime < timeout) {
    if (service.isConnected()) {
      return true;
    }
    if (isSuperseded()) return false;
    // Wait before checking again
    await new Promise((resolve) => setTimeout(resolve, checkInterval));
  }

  return false;
}

export const leaveJitsiMeeting = async ({ leaveReason }: { leaveReason?: string } = {}) => {
  const { subscription } = useMeetingStore.getState();
  if (subscription) subscription.unsubscribe();

  if (jitsiService) {
    try {
      await jitsiService.leaveMeeting({ leaveReason });
    } catch (err) {
      console.error(err);
    }
  }

  useMeetingStore.setState({
    subscription: null,
    jitsiParticipants: {},
    // Clear the local join status so no stale connecting/retry tile carries into the next
    // room — but only when no join is currently in flight. If a join is in flight, this
    // leave is happening as part of a re-switch fired mid-load (e.g. A -> B -> C before B
    // finished connecting): joinInProgress's queued request (see pendingJoinRequest above)
    // will pick up where this leaves off, so clearing status to 'idle' here would only
    // flash the loading skeleton away for a room switch that's still genuinely in progress.
    ...(joinInProgress ? {} : { meetingJoinStatus: 'idle' as const, lastJoinRequest: null }),
  });
  // NGE-681: clear auditorium mode so the next (regular) room isn't treated as one.
  useMeetingStore.getState().setAuditoriumMode(false);
  // NGE-1279: an ended session gives the floor map back even when the user stays in the
  // auditorium room (a moderator ending it from the schedule panel is not sent home).
  if (leaveReason === 'auditorium-ended') restoreFloorMapAfterAuditorium();
  // NGE-1078: and stop watching for our own role changes in it.
  stopAuditoriumRoleSync();
  useMeetingStore.getState().resetParticipantSpeaking();
};

export const getParticipantAudioTrack = (participantId: string) => {
  const state = useMeetingStore.getState();
  const participant = state.jitsiParticipants[participantId];
  if (!participant) return null;

  return participant.audioTrack ? participant.audioTrack.track : null;
};

export const useWhisperTrack = () => {
  const participants = useMeetingStore((s) => s.whisperRoomParticipants);

  const whisperTrack = useMemo(() => {
    const myUserId = getJitsiWhisperService()?.getMyUserId();
    if (!myUserId) return null;

    const entry = Object.values(participants || {}).find(
      (p: any) => myUserId && p?.id !== myUserId
    );

    return entry?.track || null;
  }, [participants]);

  return whisperTrack;
};

export const getParticipantVideoTrack = (participantId: string) => {
  const state = useMeetingStore.getState();
  const participant = state.jitsiParticipants[participantId];
  if (!participant) return null;

  return participant.videoTrack ? participant.videoTrack.track : null;
};

export const getParticipantScreenTrack = (participantId: string) => {
  const state = useMeetingStore.getState();
  const participant = state.jitsiParticipants[participantId];
  if (!participant) return null;

  return participant.screenTrack ? participant.screenTrack.track : null;
};

/**
 * Drop a participant's screen-share slot, removing its tile.
 *
 * Needed because a *receiving* MediaStreamTrack stays `readyState: 'live'` and unmuted even
 * when no media flows through it, so neither of those can prove a remote share is really
 * alive. When the tile proves it by never rendering a frame (see
 * ParticipantScreenShareView's watchdog), this is how it gets retired.
 */
export const clearParticipantScreenTrack = (participantId: string) => {
  useMeetingStore.setState((state) => {
    const participant = state.jitsiParticipants[participantId];
    if (!participant?.screenTrack) return state;

    Logger.info('[Jitsi] Clearing a screen share that never delivered frames for', participantId);
    return {
      jitsiParticipants: {
        ...state.jitsiParticipants,
        [participantId]: { ...participant, screenTrack: undefined },
      },
    };
  });
};

export const getParticipantIdByTrack = (track: any) => {
  return typeof track.getParticipantId === 'function'
    ? (track.getParticipantId() ?? jitsiService?.getMyUserId())
    : jitsiService?.getMyUserId();
};

export const getTrackType = (track: any) => track.type;
export const isAudioTrack = (track: any) => getTrackType(track) === 'audio';
export const isVideoTrack = (track: any) => getTrackType(track) === 'video';
export const isScreenTrack = (track: any) => {
  return getTrackType(track) === 'video' && track.videoType === 'desktop';
};
